import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getMe } from "../features/authSlice";

import { FaArrowLeft, FaUpload, FaLeaf, FaUser, FaBriefcase, FaFileAlt, FaLink, FaImage, FaTrash } from 'react-icons/fa';

import { useStateContext } from '../contexts/ContextProvider';

const AddTeam = () => {
  const [nama, setNama] = useState("");
  const [role, setRole] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef(null);
  const { currentColor, currentMode } = useStateContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // Colors
  const isDark = currentMode === 'Dark';
  const accentColor = currentColor || '#c9862a';
  const bgColor = isDark ? '#1a1a18' : '#f7f3ec';
  const cardBg = isDark ? '#242420' : '#ffffff';
  const borderColor = isDark ? '#2e2e28' : '#e8e0d0';
  const textPrimary = isDark ? '#f0ece3' : '#1a1a18';
  const textSecondary = isDark ? '#a09880' : '#6b6456';
  const inputBg = isDark ? '#1a1a18' : '#ffffff';
  const inputBorder = isDark ? '#3a3a34' : '#e8e0d0';


  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      console.log("berhasil");
    } else {
      navigate("/");
    }
  }, [navigate, user]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!nama.trim()) {
      newErrors.nama = "Nama lengkap wajib diisi";
    }

    if (!role) {
      newErrors.role = "Role/jabatan wajib dipilih";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  // Process file
  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  // Handle remove image
  const handleRemoveImage = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle click on upload area
  const handleUploadClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    fileInputRef.current?.click();
  };

  // Save team
  const saveTeam = async (e) => {
    e.preventDefault();

    // Validate
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("nama", nama.trim());
    formData.append("role", role);
    formData.append("deskripsi", deskripsi.trim());
    formData.append("linkedin", linkedin.trim());

    if (file) {
      formData.append("file", file);
    }

    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.post(
        `${apiUrl}/team`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      navigate("/list-team");
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message
      );
      alert(error.response?.data?.message || "Gagal menyimpan data team");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaLeaf
            className="absolute top-20 left-10 text-8xl"
            style={{ color: accentColor }}
          />
          <FaImage
            className="absolute bottom-20 right-20 text-7xl"
            style={{ color: accentColor }}
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/list-team')}
            className="flex items-center gap-2 mb-4 transition-all duration-300 hover:opacity-80"
            style={{ color: textSecondary }}
          >
            <FaArrowLeft className="text-sm" />
            <span className="text-sm font-medium">Kembali ke Daftar Team</span>
          </button>

          <div
            className="p-6 rounded-xl border"
            style={{
              backgroundColor: cardBg,
              borderColor: borderColor
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: accentColor }}
              >
                <FaUser className="text-white text-xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                    Team Management
                  </span>
                  <span className="text-xs" style={{ color: textSecondary }}>—</span>
                  <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                    Rumoh Pangan Aceh
                  </span>
                </div>
                <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                  Tambah Team Baru
                </h1>
                <p className="text-sm mt-1" style={{ color: textSecondary }}>
                  Tambahkan anggota team baru untuk ditampilkan di website
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={saveTeam}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Image Upload */}
            <div className="lg:col-span-1">
              <div
                className="p-6 rounded-xl border sticky top-6"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor
                }}
              >
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: textPrimary }}>
                  <FaImage className="text-sm" style={{ color: accentColor }} />
                  Foto Profil
                </h3>

                {/* Image Upload Area */}
                <div
                  className="relative rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden cursor-pointer"
                  style={{
                    borderColor: isDragging ? accentColor : inputBorder,
                    backgroundColor: isDragging ? `${accentColor}15` : inputBg
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  {preview ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={handleUploadClick}
                          className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          <FaUpload className="text-white text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="p-2 rounded-lg bg-red-500/80 hover:bg-red-500 transition-colors"
                        >
                          <FaTrash className="text-white text-sm" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <FaUpload className="text-2xl" style={{ color: accentColor }} />
                      </div>
                      <p className="text-sm font-medium text-center mb-1" style={{ color: textPrimary }}>
                        Drag & Drop gambar di sini
                      </p>
                      <p className="text-xs text-center" style={{ color: textSecondary }}>
                        atau klik untuk memilih file
                      </p>
                      <p className="text-[10px] mt-3 px-3 py-1 rounded-full" style={{ color: textSecondary, backgroundColor: `${accentColor}15` }}>
                        Format: JPG, PNG (Maks. 2MB)
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Form Fields */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info Card */}
              <div
                className="p-6 rounded-xl border"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor
                }}
              >
                <h3 className="font-semibold mb-6 flex items-center gap-2" style={{ color: textPrimary }}>
                  <FaUser className="text-sm" style={{ color: accentColor }} />
                  Informasi Dasar
                </h3>

                {/* Nama */}
                <div className="mb-5">
                  <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                    Nama Lengkap <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="nama"
                      value={nama}
                      onChange={(e) => setNama(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                      style={{
                        backgroundColor: inputBg,
                        borderColor: errors.nama ? '#ef4444' : inputBorder,
                        color: textPrimary,
                      }}
                      onFocus={(e) => e.target.style.borderColor = accentColor}
                      onBlur={(e) => e.target.style.borderColor = errors.nama ? '#ef4444' : inputBorder}
                    />
                    <FaUser
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: textSecondary }}
                    />
                  </div>
                  {errors.nama && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                      {errors.nama}
                    </p>
                  )}
                </div>

                {/* Role */}
                <div className="mb-5">
                  <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                    Role / Jabatan <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none appearance-none cursor-pointer"
                      style={{
                        backgroundColor: inputBg,
                        borderColor: errors.role ? '#ef4444' : inputBorder,
                        color: textPrimary,
                      }}
                      onFocus={(e) => e.target.style.borderColor = accentColor}
                      onBlur={(e) => e.target.style.borderColor = errors.role ? '#ef4444' : inputBorder}
                    >
                      <option value="">Pilih role/jabatan</option>
                      <option value="Founder and Excutive Director">Founder and Excutive Director</option>
                      <option value="Finance and Adminstration">Finance and Adminstration</option>
                      <option value="Program Manager">Program Manager</option>
                      <option value="FLW Coordinator">FLW Coordinator</option>
                      <option value="Research and Education Coordinator">Research and Education Coordinator</option>
                      <option value="Farmers Coordinator">Farmers Coordinator</option>
                      <option value="Food Aid for Disaster Response Coordinator">Food Aid for Disaster Response Coordinator</option>
                      <option value="Intern">Intern</option>
                    </select>
                    <FaBriefcase
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: textSecondary }}
                    />
                    <div
                      className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                      style={{ color: textSecondary }}
                    >
                      ▼
                    </div>
                  </div>
                  {errors.role && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                      {errors.role}
                    </p>
                  )}
                </div>

                {/* Deskripsi */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                    Deskripsi / Bio
                  </label>
                  <div className="relative">
                    <textarea
                      name="deskripsi"
                      value={deskripsi}
                      onChange={(e) => setDeskripsi(e.target.value)}
                      placeholder="Tuliskan deskripsi singkat tentang diri Anda, pengalaman, dan kontribusi..."
                      rows={5}
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none resize-none"
                      style={{
                        backgroundColor: inputBg,
                        borderColor: errors.deskripsi ? '#ef4444' : inputBorder,
                        color: textPrimary,
                      }}
                      onFocus={(e) => e.target.style.borderColor = accentColor}
                      onBlur={(e) => e.target.style.borderColor = errors.deskripsi ? '#ef4444' : inputBorder}
                    />
                    <FaFileAlt
                      className="absolute left-4 top-4 text-sm"
                      style={{ color: textSecondary }}
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    {errors.deskripsi && (
                      <p className="text-xs" style={{ color: '#ef4444' }}>
                        {errors.deskripsi}
                      </p>
                    )}
                    <p className={`text-xs ml-auto ${deskripsi.length > 500 ? 'text-red-500' : ''}`} style={{ color: textSecondary }}>
                      {deskripsi.length}/500 karakter
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links Card */}
              <div
                className="p-6 rounded-xl border"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor
                }}
              >
                <h3 className="font-semibold mb-6 flex items-center gap-2" style={{ color: textPrimary }}>
                  <FaLink className="text-sm" style={{ color: accentColor }} />
                  Tautan Sosial Media
                </h3>

                {/* LinkedIn */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                    LinkedIn Profile
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      name="linkedin"
                      value={linkedin}
                      onChange={(e) => setLinkedin(e.target.value)}
                      placeholder="https://linkedin.com/in/username"
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                      style={{
                        backgroundColor: inputBg,
                        borderColor: errors.linkedin ? '#ef4444' : inputBorder,
                        color: textPrimary,
                      }}
                      onFocus={(e) => e.target.style.borderColor = accentColor}
                      onBlur={(e) => e.target.style.borderColor = errors.linkedin ? '#ef4444' : inputBorder}
                    />
                    <FaLink
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: textSecondary }}
                    />
                  </div>
                  {errors.linkedin && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                      {errors.linkedin}
                    </p>
                  )}
                  <p className="text-xs mt-2" style={{ color: textSecondary }}>
                    Tempelkan URL profil LinkedIn Anda
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="p-6 rounded-xl border flex flex-col sm:flex-row gap-4 justify-end"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate('/list-team')}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: isDark ? '#1a1a18' : '#f7f3ec',
                    color: textPrimary,
                    border: `1px solid ${borderColor}`
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = isDark ? '#282C33' : '#e8e0d0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = isDark ? '#1a1a18' : '#f7f3ec'}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: accentColor
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#b5772a'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = accentColor}
                >
                  <FaLeaf className="text-sm" />
                  {isSubmitting ? 'Menyimpan...' : 'Simpan Team'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTeam;